import React, { useState } from 'react';
import Icon from '../shared/Icon';
import './DashboardTabBar.css';
import DashboardTabMenu from './DashboardTabMenu';
import TabStateIcon from './TabStateIcon';
import { MAX_TAB_NAME_LENGTH, MAX_TABS, TAB_STATE, TAB_STATE_ICON, ownTabs } from './tabsUtils';

function tooltipFor(tab) {
  if (tab.state === TAB_STATE.SHARED_WITH_YOU) return `Shared with you by ${tab.owner_name || 'a colleague'}`;
  return TAB_STATE_ICON[tab.state]?.label;
}

/**
 * The Insights tab bar: the user's tabs in their order, then New tab. Presentational —
 * every change goes through the callbacks, which own the server calls.
 */
function DashboardTabBar({
  tabs, activeTabId, canShare, onSelect, onCreate, onRename, onShare, onMove, onDelete, onHide,
}) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [dragId, setDragId] = useState(null);
  const ownCount = ownTabs(tabs).length;

  const startRename = (tab) => {
    setEditingId(tab.id);
    setDraft(tab.name);
  };
  const commitRename = async (tab) => {
    if (draft.trim() === tab.name) {
      setEditingId(null);
      return;
    }
    if (await onRename(tab.id, draft)) setEditingId(null);
  };
  const handleCreate = async () => {
    const tab = await onCreate();
    if (tab) startRename(tab);
  };

  return (
    <div className="dtb" role="tablist" aria-label="Dashboard tabs">
      {tabs.map((tab, index) => {
        const active = tab.id === activeTabId;
        return (
          <div
            key={tab.id}
            className={`dtb-tab${active ? ' dtb-tab--active' : ''}${dragId === tab.id ? ' dtb-tab--dragging' : ''}`}
            draggable={editingId !== tab.id}
            onDragStart={(e) => { setDragId(tab.id); e.dataTransfer.effectAllowed = 'move'; }}
            onDragEnd={() => setDragId(null)}
            onDragOver={(e) => { if (dragId) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); if (dragId && dragId !== tab.id) onMove(dragId, index); setDragId(null); }}
          >
            {editingId === tab.id ? (
              <input
                className="dtb-rename"
                aria-label="Tab name"
                value={draft}
                maxLength={MAX_TAB_NAME_LENGTH}
                // Focus follows the user's own action (New tab or Rename), not page load.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename(tab);
                  if (e.key === 'Escape') setEditingId(null);
                }}
                onBlur={() => commitRename(tab)}
              />
            ) : (
              <button
                type="button"
                role="tab"
                aria-selected={active}
                className="dtb-tab-btn"
                title={tooltipFor(tab)}
                onClick={() => onSelect(tab.id)}
                onDoubleClick={() => tab.is_own && startRename(tab)}
              >
                <TabStateIcon state={tab.state} />
                <span className="dtb-tab-name">{tab.name}</span>
              </button>
            )}
            {active && editingId !== tab.id && (
              <DashboardTabMenu
                tab={tab}
                index={index}
                count={tabs.length}
                canShare={canShare}
                canDelete={ownCount > 1}
                onRename={() => startRename(tab)}
                onShare={(share) => onShare(tab, share)}
                onMove={(to) => onMove(tab.id, to)}
                onDelete={() => onDelete(tab)}
                onHide={() => onHide(tab)}
              />
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="dtb-new"
        onClick={handleCreate}
        disabled={ownCount >= MAX_TABS}
        title={ownCount >= MAX_TABS ? `You can have at most ${MAX_TABS} tabs` : undefined}
      >
        <Icon name="add" />
        New tab
      </button>
    </div>
  );
}

export default DashboardTabBar;
